import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, Skeleton } from '@/design-system';
import { orderApi, orderKeys } from '@/entities/order';
import {
    approvalWaitsForInspector,
    approvalWaitsForSurvey,
    canApprove,
    canEditTexts,
    hasEarlierReleasedVersion,
    isGenerating,
    reportAccessFor,
    sectionByUuid,
    type Report,
    type ReportApproval,
    type ReportInvoiceChoice,
} from '@/entities/report';
import { problemKey, useSessionStore } from '@/shared/api';
import { formatDayTimeShort } from '@/shared/lib';
import { ErrorState, Screen, ScreenBody, TabHeader } from '@/shared/ui';

import { ApprovalConfirm } from './components/ApprovalConfirm';
import { ApprovalResult } from './components/ApprovalResult';
import { CorrectionConfirm } from './components/CorrectionConfirm';
import { NewVersionConfirm } from './components/NewVersionConfirm';
import { ReportDocument } from './components/ReportDocument';
import { ReleasedVersionNotice } from './components/ReleasedVersionNotice';
import { ReportLinkCard } from './components/ReportLinkCard';
import { ReportMoreMenu } from './components/ReportMoreMenu';
import { ReportStateCard } from './components/ReportStateCard';
import { SectionList } from './components/SectionList';
import { SendConfirm } from './components/SendConfirm';
import { SurveyJumpProvider } from './components/SurveyJump';
import { reportCopy } from './copy';
import { invoiceResultLine } from './invoiceResult';
import type { InvoiceEnclosureSetup } from './useInvoiceChoice';
import { reportProblemMessage, useReportScreen } from './useReportScreen';

/** De uitleg waar de uitgeschakelde knoppen naar verwijzen. */
const SURVEY_OPEN_NOTICE = 'rapport-opname-open';

/** De uitleg bij een goedkeurknop die wacht op het akkoord van de inspecteur. */
const AWAITING_INSPECTOR_NOTICE = 'rapport-wacht-op-inspecteur';

/**
 * Scherm 9 - het tabblad Rapport van een keuring.
 *
 * Het staat onder de kop van de keuring (`OrderLayout`), die zegt om welke
 * keuring het gaat. De stand van het rapport en de knoppen staan in die kop.
 *
 * Links de secties, rechts hoe het rapport eruitziet. Het concept komt uit de
 * opname (in de wachtrij van de API); teksten zijn ter plekke bij te stellen
 * zolang het een concept is.
 *
 * **Goedkeuren is versturen** (principe 5). Die knop opent daarom eerst een
 * bevestiging die zegt naar wie het gaat en dat het onomkeerbaar is. Daarna
 * staat de toegangscode één keer in beeld: de API geeft hem nooit opnieuw.
 * Opnieuw versturen vraagt net zo eerst om bevestiging.
 *
 * De kop heeft hoogstens drie knoppen: de hoofdactie van deze stand, hoogstens één
 * andere, en "Meer" met het rapport bekijken, de PDF en de klantpagina. Een
 * nieuwe versie van een vastgesteld rapport is één knop; in de bevestiging kies je
 * of hij begint met je eigen teksten of opnieuw uit de opname.
 *
 * In beide bevestigingen kan de factuur mee ("Factuur meesturen", met een eigen
 * bericht), voor wie facturen mag opstellen en uitgeven: de klant krijgt dan rapport
 * en factuur in één mail.
 */
export function ReportPage() {
    const { orderId = '' } = useParams<{ orderId: string }>();
    const navigate = useNavigate();
    const session = useSessionStore((state) => state.session);
    const access = reportAccessFor(session);
    const [confirming, setConfirming] = useState<
        'approve' | 'send' | 'regenerate' | 'newVersion' | null
    >(null);
    const screen = useReportScreen(orderId, {
        invoice: access.invoice && (confirming === 'approve' || confirming === 'send'),
    });
    /*
     * Dezelfde query als de kop van de keuring: de opname erachter (en haar stand)
     * bepaalt of "Aanpassen in de opname" meteen kan of eerst heropenen vraagt.
     */
    const order = useQuery({
        queryKey: orderKeys.detail(orderId),
        queryFn: () => orderApi.show(orderId),
        enabled: orderId !== '',
    });

    const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
    const [approval, setApproval] = useState<ReportApproval | null>(null);
    /* Of bij de laatste verzending om de factuur was gevraagd: dan zegt de uitkomst of ze meeging. */
    const [invoiceRequested, setInvoiceRequested] = useState(false);
    /* Opnieuw versturen dat een nieuwe link met code maakte: die code staat hier één keer. */
    const [resent, setResent] = useState<ReportApproval | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    /* Geen ontvanger met een e-mailadres: de melding krijgt de weg naar "Klant bewerken". */
    const [problemNeedsClient, setProblemNeedsClient] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);

    const orderReport = screen.report.data;
    const data = orderReport === undefined ? undefined : (orderReport?.report ?? null);
    const report = data ?? null;
    /* Na "Nieuwe versie maken": de versie die de koper nog heeft. */
    const earlierReleased =
        orderReport && hasEarlierReleasedVersion(orderReport) ? orderReport.released : null;
    /*
     * Een concept dat de werkende link van de koper overneemt: vaststellen zet het
     * meteen bij hem neer, en een bericht erover is een keuze. Werkt zijn link niet
     * meer, dan krijgt de nieuwe versie een eigen link en gaat hij uit als de eerste.
     */
    const correctionOf =
        earlierReleased !== null && earlierReleased.link !== null && report?.status === 'concept'
            ? earlierReleased
            : null;
    const busy =
        screen.generate.isPending ||
        screen.approve.isPending ||
        screen.send.isPending ||
        screen.revise.isPending;
    /*
     * Wacht de server op meer PDF's dan deze, dan staat dat onder de melding:
     * dan is het niet dit rapport dat stuk is, maar de dienst die ontbreekt.
     */
    const pdfBacklog = screen.pdfService.data?.waiting ?? 0;
    /* De vaste link van de versie die hier staat; die deel je met de koper. */
    const ownLink =
        orderReport?.released?.reportUuid === report?.uuid
            ? (orderReport?.released?.link ?? null)
            : null;

    /*
     * Verstuurt dit bedrijf zonder nalezen, dan gaat een concept dat vanzelf volgt
     * op de opname meteen naar de klant. Een versie die hier gevraagd wordt niet:
     * die blijft een concept tot iemand hem goedkeurt.
     */
    const autoSend = screen.settings.data?.autoSendReport ?? false;

    /*
     * Factuur meesturen: hoe de factuur van de keuring ervoor staat en het standaard
     * eigen bericht. Lukte het opvragen niet, dan zegt het blok dat het rapport zonder
     * factuur gaat.
     */
    const invoiceSetup: InvoiceEnclosureSetup | undefined = access.invoice
        ? {
              option: screen.orderInvoice.isError ? null : screen.orderInvoice.data,
              defaultMessage: screen.settings.data?.invoiceWithReportMessage ?? '',
          }
        : undefined;

    const settle = {
        onError: (error: unknown) => {
            setNotice(null);
            setProblem(reportProblemMessage(error));
            setProblemNeedsClient(problemKey(error) === 'report-without-recipients');
        },
    };

    /** "Klant bewerken" in het dossier van deze keuring; daar vul je het e-mailadres in. */
    const editClient = access.edit ? () => navigate(paths.dossierEditClient(orderId)) : undefined;

    const generate = () => {
        setConfirming(null);
        setProblem(null);
        screen.generate.mutate(undefined, {
            ...settle,
            onSuccess: () => {
                setApproval(null);
                setNotice(null);
            },
        });
    };

    /**
     * Zonder gevolgen voor de klant en zonder verlies van eigen tekst gaat
     * genereren meteen; anders eerst een bevestiging die zegt wat er gebeurt.
     */
    const askToGenerate = (current: Report | null) => {
        const edited = current?.sections.some((section) => !section.bodyIsGenerated) ?? false;
        const harmless =
            current === null || current.state === 'failed' || current.status === 'concept';

        if (harmless && !edited) {
            generate();

            return;
        }

        setProblem(null);
        setConfirming('regenerate');
    };

    const approve = (
        current: Report,
        send: boolean,
        invoice?: ReportInvoiceChoice,
        language?: string,
    ) => {
        setProblem(null);
        setInvoiceRequested(invoice !== undefined);
        screen.approve.mutate(
            {
                reportUuid: current.uuid,
                send,
                ...(invoice ? { invoice } : {}),
                ...(language ? { language } : {}),
            },
            {
                ...settle,
                onSuccess: (result) => {
                    setConfirming(null);
                    setApproval(result);
                },
                onSettled: (_result, error) => {
                    if (error) {
                        setConfirming(null);
                    }
                },
            },
        );
    };

    /** Een vastgesteld rapport weer openzetten, met de eigen teksten; de klant houdt zijn link. */
    const revise = (current: Report) => {
        setProblem(null);
        setConfirming(null);
        screen.revise.mutate(current.uuid, {
            ...settle,
            onSuccess: (next) => {
                setApproval(null);
                setResent(null);
                setNotice(reportCopy.reviseDone(next.version));
            },
        });
    };

    const resend = (current: Report, invoice?: ReportInvoiceChoice, language?: string) => {
        setProblem(null);
        setInvoiceRequested(invoice !== undefined);
        screen.send.mutate(
            {
                reportUuid: current.uuid,
                ...(invoice ? { invoice } : {}),
                ...(language ? { language } : {}),
            },
            {
                ...settle,
                onSuccess: ({ report: next, link, newLink, delivery, invoice: enclosed }) => {
                    setConfirming(null);
                    setNotice(
                        [
                            delivery.failed > 0
                                ? reportCopy.resendPartial(delivery.sent, delivery.failed)
                                : reportCopy.resendDone(delivery.sent),
                            invoiceResultLine(enclosed, invoice !== undefined),
                        ]
                            .filter(Boolean)
                            .join(' '),
                    );
                    setResent(newLink ? { report: next, link, delivery, invoice: enclosed } : null);
                },
                onSettled: (_result, error) => {
                    if (error) {
                        setConfirming(null);
                    }
                },
            },
        );
    };

    /** Opnieuw versturen: eerst de bevestiging, met de ontvangers en de factuur. */
    const askToSend = () => {
        setProblem(null);
        setNotice(null);
        setConfirming('send');
    };

    const fetchPdf = (current: Report, mode: 'download' | 'view') => {
        setProblem(null);
        screen.pdf.mutate({ report: current, mode }, settle);
    };

    const openClientPage = (current: Report) => navigate(paths.clientPage(current.orderUuid));

    /** "Nieuwe versie": eerst de bevestiging, waarin je kiest hoe de versie begint. */
    const askNewVersion = () => {
        setProblem(null);
        setNotice(null);
        setConfirming('newVersion');
    };

    return (
        <Screen>
            {/* Een Context.Provider zet geen element neer: `ScreenBody` blijft kind van `Screen`. */}
            <SurveyJumpProvider order={order.data ?? null}>
                <TabHeader status={eyebrowFor(data)} actions={headerActions()} />

                <ScreenBody>
                    {screen.report.isError ? (
                        <ErrorState
                            error={screen.report.error}
                            title={reportCopy.errorTitle}
                            onRetry={() => void screen.report.refetch()}
                        />
                    ) : null}

                    {problem ? (
                        <div className="mb-[14px] max-w-[780px]">
                            <div className="flex flex-wrap items-center gap-3">
                                <p
                                    role="alert"
                                    className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]"
                                >
                                    {problem}
                                </p>
                                {problemNeedsClient && editClient ? (
                                    <Button variant="outline" size="sm" onClick={editClient}>
                                        {reportCopy.editClient}
                                    </Button>
                                ) : null}
                            </div>
                            {pdfBacklog > 0 ? (
                                <p className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                                    {reportCopy.pdfBacklog(pdfBacklog)}
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {screen.pdf.isPending ? (
                        <p
                            role="status"
                            className="m-0 mb-[14px] text-[13px] leading-[1.5] text-[var(--text-secondary)]"
                        >
                            {reportCopy.pdfBusy}
                        </p>
                    ) : null}

                    {notice ? (
                        <p
                            role="status"
                            className="m-0 mb-[14px] text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                        >
                            {notice}
                        </p>
                    ) : null}

                    {approval ? (
                        <ApprovalResult
                            approval={approval}
                            invoiceRequested={invoiceRequested}
                            onClose={() => setApproval(null)}
                        />
                    ) : null}

                    {resent ? (
                        <ApprovalResult
                            approval={resent}
                            kind="resend"
                            invoiceRequested={invoiceRequested}
                            onClose={() => setResent(null)}
                        />
                    ) : null}

                    {confirming === 'approve' && report && correctionOf ? (
                        <CorrectionConfirm
                            version={report.version}
                            releasedVersion={correctionOf.version}
                            recipients={report.recipients}
                            busy={screen.approve.isPending}
                            invoice={invoiceSetup}
                            languages={report.clientLanguages}
                            onConfirm={(notify, invoice, language) =>
                                approve(report, notify, invoice, language)
                            }
                            onCancel={() => setConfirming(null)}
                        />
                    ) : confirming === 'approve' && report ? (
                        <ApprovalConfirm
                            recipients={report.recipients}
                            busy={screen.approve.isPending}
                            invoice={invoiceSetup}
                            languages={report.clientLanguages}
                            onConfirm={(invoice, language) =>
                                approve(report, true, invoice, language)
                            }
                            onRelease={() => approve(report, false)}
                            onCancel={() => setConfirming(null)}
                            onEditClient={editClient}
                        />
                    ) : null}

                    {confirming === 'send' && report ? (
                        <SendConfirm
                            recipients={report.recipients}
                            busy={screen.send.isPending}
                            invoice={invoiceSetup}
                            languages={report.clientLanguages}
                            onConfirm={(invoice, language) => resend(report, invoice, language)}
                            onCancel={() => setConfirming(null)}
                        />
                    ) : null}

                    {confirming === 'newVersion' && report ? (
                        <NewVersionConfirm
                            version={report.version}
                            busy={screen.revise.isPending || screen.generate.isPending}
                            rebuildBlockedBy={report.surveyOpen ? SURVEY_OPEN_NOTICE : undefined}
                            onConfirm={(start) => (start === 'keep' ? revise(report) : generate())}
                            onCancel={() => setConfirming(null)}
                        />
                    ) : null}

                    {confirming === 'regenerate' && data !== undefined ? (
                        <ReportStateCard
                            tone="warn"
                            title={reportCopy.regenerateTitle}
                            action={
                                <>
                                    <Button
                                        variant="primary"
                                        size="md"
                                        disabled={screen.generate.isPending}
                                        onClick={generate}
                                    >
                                        {reportCopy.regenerateConfirm}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="md"
                                        disabled={screen.generate.isPending}
                                        onClick={() => setConfirming(null)}
                                    >
                                        {reportCopy.regenerateCancel}
                                    </Button>
                                </>
                            }
                        >
                            {[
                                report && report.state !== 'failed'
                                    ? reportCopy.regenerateBody(report.version)
                                    : null,
                                report && report.status !== 'concept'
                                    ? reportCopy.regenerateStaysConcept(report.version)
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        </ReportStateCard>
                    ) : null}

                    {earlierReleased && report ? (
                        <ReleasedVersionNotice
                            released={earlierReleased}
                            newestVersion={report.version}
                            onOpenClientPage={() => openClientPage(report)}
                        />
                    ) : null}

                    {!earlierReleased &&
                    !approval &&
                    !resent &&
                    report &&
                    report.status !== 'concept' ? (
                        <ReportLinkCard
                            link={ownLink}
                            /* Een correctie zonder bericht staat ook al bij de klant: via zijn link. */
                            sent={report.sentAt !== null || report.replacesVersion !== null}
                            onOpenClientPage={() => openClientPage(report)}
                        />
                    ) : null}

                    {content()}
                </ScreenBody>
            </SurveyJumpProvider>
        </Screen>
    );

    function content() {
        if (data === undefined) {
            return screen.report.isError ? null : (
                <Skeleton height={420} radius={10} label={reportCopy.screenTitle} />
            );
        }

        if (data === null) {
            return (
                <ReportStateCard tone="default" title={reportCopy.noneTitle}>
                    {access.edit ? reportCopy.noneBody : reportCopy.noneReadOnly}
                </ReportStateCard>
            );
        }

        if (isGenerating(data)) {
            return (
                <ReportStateCard tone="accent" role="status" title={reportCopy.generatingTitle}>
                    {autoSend ? reportCopy.generatingAutoSendBody : reportCopy.generatingBody}
                </ReportStateCard>
            );
        }

        if (data.state === 'failed') {
            return (
                <ReportStateCard tone="warn" role="alert" title={reportCopy.failedTitle}>
                    {reportCopy.failedBody}
                </ReportStateCard>
            );
        }

        const section = sectionByUuid(data, selectedUuid);

        return (
            <>
                {data.status === 'concept' && data.surveyOpen ? (
                    <div id={SURVEY_OPEN_NOTICE}>
                        <ReportStateCard
                            tone="warn"
                            role="status"
                            title={reportCopy.surveyOpenTitle}
                        >
                            {approvalWaitsForSurvey(data) || data.can.edit
                                ? reportCopy.surveyOpenBody
                                : reportCopy.surveyOpenReadOnly}
                        </ReportStateCard>
                    </div>
                ) : null}

                {data.status === 'concept' &&
                !data.surveyOpen &&
                data.adjustmentsAwaitingInspector > 0 ? (
                    <div id={AWAITING_INSPECTOR_NOTICE}>
                        <ReportStateCard
                            tone="warn"
                            role="status"
                            title={reportCopy.surveyAwaitsInspectorTitle}
                        >
                            {data.can.confirm
                                ? reportCopy.awaitingInspectorSelf(
                                      data.adjustmentsAwaitingInspector,
                                  )
                                : reportCopy.awaitingInspectorOffice(
                                      data.adjustmentsAwaitingInspector,
                                  )}
                        </ReportStateCard>
                    </div>
                ) : null}

                {data.status !== 'concept' && data.surveyOpen ? (
                    <div id={SURVEY_OPEN_NOTICE}>
                        <ReportStateCard
                            tone="warn"
                            role="status"
                            title={reportCopy.surveyOpenTitle}
                        >
                            {reportCopy.surveyOpenReleasedBody}
                        </ReportStateCard>
                    </div>
                ) : null}

                {data.photosUnprocessed.failed > 0 ? (
                    <ReportStateCard
                        tone="warn"
                        role="status"
                        title={reportCopy.photosFailedTitle(data.photosUnprocessed.failed)}
                    >
                        {data.status === 'concept'
                            ? reportCopy.photosFailedConcept(data.photosUnprocessed.failed)
                            : reportCopy.photosFailedReleased(data.photosUnprocessed.failed)}
                    </ReportStateCard>
                ) : null}

                {data.sentAt ? (
                    <p
                        role="status"
                        className="m-0 mb-[14px] text-[13px] text-[var(--accent-dark)]"
                    >
                        {reportCopy.sentNotice(formatDayTimeShort(data.sentAt))}
                    </p>
                ) : data.status === 'goedgekeurd' && data.replacesVersion !== null ? (
                    <p
                        role="status"
                        className="m-0 mb-[14px] text-[13px] text-[var(--accent-dark)]"
                    >
                        {reportCopy.approvedBehindLinkNotice(data.version, data.replacesVersion)}
                    </p>
                ) : data.status === 'goedgekeurd' ? (
                    <p role="status" className="m-0 mb-[14px] text-[13px] text-[var(--warn-ink)]">
                        {reportCopy.approvedNotice}
                    </p>
                ) : null}

                <div className="flex flex-wrap items-start gap-[14px]">
                    <SectionList
                        sections={data.sections}
                        selectedUuid={section?.uuid ?? null}
                        onSelect={(next) => setSelectedUuid(next.uuid)}
                    />
                    <ReportDocument
                        report={data}
                        section={section}
                        editable={canEditTexts(data)}
                        saving={screen.saveSection.isPending}
                        saveError={
                            screen.saveSection.isError
                                ? reportProblemMessage(screen.saveSection.error)
                                : null
                        }
                        logoUrl={session?.tenant?.logoUrl ?? null}
                        onSaveSection={(target, body) =>
                            screen.saveSection.mutate({
                                reportUuid: data.uuid,
                                sectionUuid: target.uuid,
                                body,
                            })
                        }
                        onAdjustFinding={(finding, adjustment) =>
                            screen.adjustFinding.mutateAsync({
                                reportUuid: data.uuid,
                                findingKey: finding.key,
                                adjustment,
                            })
                        }
                        onConfirmFinding={(finding) =>
                            screen.confirmFinding.mutateAsync({
                                reportUuid: data.uuid,
                                findingKey: finding.key,
                            })
                        }
                    />
                </div>
            </>
        );
    }

    function headerActions() {
        if (data === undefined) {
            return null;
        }

        if (data === null) {
            return access.edit ? (
                <Button
                    variant="primary"
                    size="sm"
                    disabled={busy || confirming !== null}
                    onClick={() => askToGenerate(null)}
                >
                    {reportCopy.generate}
                </Button>
            ) : null;
        }

        const current = data;
        /* Zolang er geen leesbaar concept is, valt er niets te bekijken of als PDF te maken. */
        const unreadable = isGenerating(current) || current.state === 'failed';
        const pdfDisabled = unreadable || screen.pdf.isPending;
        const pdfHint = isGenerating(current) ? reportCopy.pdfWaiting : undefined;
        const blocked = busy || confirming !== null;
        /* Lezen wat de koper leest, de PDF en de klantpagina: in "Meer", niet als eigen knoppen. */
        const more = (
            <ReportMoreMenu<'view' | 'pdfView' | 'pdfDownload' | 'clientPage'>
                items={[
                    {
                        value: 'view',
                        label: reportCopy.view.open,
                        disabled: unreadable,
                        onSelect: () => void navigate(paths.reportView(orderId)),
                    },
                    {
                        value: 'pdfView',
                        label: screen.pdf.isPending ? reportCopy.pdfBusy : reportCopy.pdfView,
                        hint: pdfHint,
                        disabled: pdfDisabled,
                        onSelect: () => fetchPdf(current, 'view'),
                    },
                    {
                        value: 'pdfDownload',
                        label: reportCopy.pdf,
                        hint: pdfHint,
                        disabled: pdfDisabled,
                        onSelect: () => fetchPdf(current, 'download'),
                    },
                    ...(current.status === 'concept' || unreadable
                        ? []
                        : [
                              {
                                  value: 'clientPage' as const,
                                  label: reportCopy.clientPage,
                                  onSelect: () => void openClientPage(current),
                              },
                          ]),
                ]}
            />
        );

        if (isGenerating(current)) {
            return more;
        }

        if (current.state === 'failed') {
            return current.can.edit ? (
                <Button
                    variant="primary"
                    size="sm"
                    disabled={blocked}
                    onClick={() => askToGenerate(current)}
                >
                    {reportCopy.regenerate}
                </Button>
            ) : null;
        }

        if (current.status === 'concept') {
            /* Zolang de opname weer open staat, weigert de API beide; de knoppen zeggen dat vooraf. */
            const waitsForSurvey = current.surveyOpen;
            /* Goedkeuren wacht ook op het akkoord van de inspecteur met aangepaste bedragen. */
            const waitsForInspector = approvalWaitsForInspector(current);

            return (
                <>
                    {more}
                    {current.can.edit ? (
                        <Button
                            variant="onDark"
                            size="sm"
                            disabled={blocked || waitsForSurvey}
                            aria-describedby={waitsForSurvey ? SURVEY_OPEN_NOTICE : undefined}
                            onClick={() => askToGenerate(current)}
                        >
                            {reportCopy.regenerate}
                        </Button>
                    ) : null}
                    {canApprove(current) ? (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={blocked || waitsForSurvey || waitsForInspector}
                            aria-describedby={
                                waitsForSurvey
                                    ? SURVEY_OPEN_NOTICE
                                    : waitsForInspector
                                      ? AWAITING_INSPECTOR_NOTICE
                                      : undefined
                            }
                            onClick={() => {
                                setProblem(null);
                                setConfirming('approve');
                            }}
                        >
                            {/* Verstuurt alleen bij de eerste versie; een correctie stel je vast. */}
                            {correctionOf
                                ? reportCopy.correctionConfirm(current.version)
                                : reportCopy.approve}
                        </Button>
                    ) : null}
                </>
            );
        }

        const newVersion = current.can.edit ? (
            <Button
                variant={current.status === 'goedgekeurd' ? 'onDark' : 'primary'}
                size="sm"
                disabled={blocked}
                onClick={askNewVersion}
            >
                {reportCopy.newVersion}
            </Button>
        ) : null;
        const resend = current.can.send ? (
            <Button
                variant={current.status === 'goedgekeurd' ? 'primary' : 'onDark'}
                size="sm"
                disabled={blocked}
                onClick={() => askToSend()}
            >
                {reportCopy.resend}
            </Button>
        ) : null;

        /*
         * Goedgekeurd maar nog bij niemand bezorgd: versturen is de hoofdactie.
         * Verstuurd: dan is het rapport bij de klant en is een nieuwe versie wat je
         * meestal nog doet.
         */
        return current.status === 'goedgekeurd' ? (
            <>
                {more}
                {newVersion}
                {resend}
            </>
        ) : (
            <>
                {more}
                {resend}
                {newVersion}
            </>
        );
    }
}

function eyebrowFor(report: Report | null | undefined): string {
    if (report === undefined) {
        return reportCopy.eyebrowLoading;
    }

    if (report === null) {
        return reportCopy.eyebrowNone;
    }

    if (isGenerating(report)) {
        return reportCopy.eyebrowGenerating(report.version);
    }

    if (report.state === 'failed') {
        return reportCopy.eyebrowFailed(report.version);
    }

    if (report.sentAt) {
        return reportCopy.eyebrowSent(report.version, formatDayTimeShort(report.sentAt));
    }

    if (report.status === 'goedgekeurd') {
        return report.replacesVersion !== null
            ? reportCopy.eyebrowApprovedBehindLink(report.version)
            : reportCopy.eyebrowApproved(report.version);
    }

    return reportCopy.eyebrowConcept(report.version, report.minutesAfterInspection);
}
